import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Heart, Sparkles } from 'lucide-react'
import type { FishDef, FishStats } from '@/data/fish'
import { DECORATIONS, type PlacedDecoration, type Decoration } from '@/data/decorations'
import type { BackgroundState } from '@/hooks/useAquarium'

interface Pellet { id: number; x: number; y: number }
interface Ripple { id: number; x: number; y: number }
interface FloatText { id: number; x: number; y: number; text: string; color: string }
interface Bubble { id: number; x: number; y: number; size: number; dur: number; drift: number }

interface TankProps {
  fish: FishDef
  lighting: 'day' | 'night'
  followMode: boolean
  feedSignal: number
  lowStatHint: string | null
  stats: FishStats
  water: number
  decorations: PlacedDecoration[]
  selectedDecoration: Decoration | null
  backgroundState: BackgroundState
  onEat: () => void
  onTease: () => void
  onPetTrickle: () => void
  onPlaceDecoration: (x: number, y: number) => void
  onCancelPlacement: () => void
  growthScale: number
  growthStage: number
  fishLevelUpTick: number
}

const rand = (min: number, max: number) => min + Math.random() * (max - min)
let uid = 1

type IdleState = 'active' | 'hover' | 'drift' | 'rest'

interface BehaviorParams {
  wanderInterval: number
  turnTilt: number
  tailWagAmplitude: number
  tailWagSpeed: number
  excitementScale: number
  moodSpeedModifier: number
  satietySpeedModifier: number
  energySpeedModifier: number
  idleChance: number
  hoverDuration: number
  driftSpeed: number
  restChance: number
  bondResponse: number
}

const TRAIT_BEHAVIOR: Record<string, BehaviorParams> = {
  高傲: {
    wanderInterval: 4200,
    turnTilt: 18,
    tailWagAmplitude: 8,
    tailWagSpeed: 0.8,
    excitementScale: 0.6,
    moodSpeedModifier: 0.3,
    satietySpeedModifier: 0.5,
    energySpeedModifier: 0.4,
    idleChance: 0.15,
    hoverDuration: 3000,
    driftSpeed: 0.3,
    restChance: 0.05,
    bondResponse: 0.5,
  },
  憨厚: {
    wanderInterval: 5500,
    turnTilt: 12,
    tailWagAmplitude: 6,
    tailWagSpeed: 0.5,
    excitementScale: 0.4,
    moodSpeedModifier: 0.2,
    satietySpeedModifier: 0.6,
    energySpeedModifier: 0.5,
    idleChance: 0.3,
    hoverDuration: 5000,
    driftSpeed: 0.2,
    restChance: 0.15,
    bondResponse: 0.8,
  },
  活泼: {
    wanderInterval: 2200,
    turnTilt: 25,
    tailWagAmplitude: 12,
    tailWagSpeed: 1.5,
    excitementScale: 1.3,
    moodSpeedModifier: 0.5,
    satietySpeedModifier: 0.3,
    energySpeedModifier: 0.3,
    idleChance: 0.05,
    hoverDuration: 1500,
    driftSpeed: 0.5,
    restChance: 0.02,
    bondResponse: 1.2,
  },
  神秘: {
    wanderInterval: 6000,
    turnTilt: 10,
    tailWagAmplitude: 5,
    tailWagSpeed: 0.4,
    excitementScale: 0.3,
    moodSpeedModifier: 0.4,
    satietySpeedModifier: 0.4,
    energySpeedModifier: 0.6,
    idleChance: 0.4,
    hoverDuration: 6000,
    driftSpeed: 0.15,
    restChance: 0.2,
    bondResponse: 0.3,
  },
  优雅: {
    wanderInterval: 4800,
    turnTilt: 15,
    tailWagAmplitude: 7,
    tailWagSpeed: 0.7,
    excitementScale: 0.7,
    moodSpeedModifier: 0.35,
    satietySpeedModifier: 0.45,
    energySpeedModifier: 0.4,
    idleChance: 0.2,
    hoverDuration: 4000,
    driftSpeed: 0.25,
    restChance: 0.08,
    bondResponse: 0.7,
  },
}

const getBehaviorParams = (trait: string): BehaviorParams => {
  return TRAIT_BEHAVIOR[trait] || {
    wanderInterval: 3600,
    turnTilt: 15,
    tailWagAmplitude: 8,
    tailWagSpeed: 1.0,
    excitementScale: 1.0,
    moodSpeedModifier: 0.4,
    satietySpeedModifier: 0.4,
    energySpeedModifier: 0.4,
    idleChance: 0.2,
    hoverDuration: 3000,
    driftSpeed: 0.3,
    restChance: 0.1,
    bondResponse: 0.7,
  }
}

function getStateSpeedMultiplier(stats: FishStats, behavior: BehaviorParams): number {
  const satietyFactor = stats.satiety / 1000
  const moodFactor = stats.mood / 1000
  const energyFactor = stats.energy / 1000
  
  return (
    0.3 +
    satietyFactor * behavior.satietySpeedModifier +
    moodFactor * behavior.moodSpeedModifier +
    energyFactor * behavior.energySpeedModifier
  )
}

function getEnvironmentModifier(lighting: 'day' | 'night', water: number): number {
  const lightFactor = lighting === 'day' ? 1.0 : 0.65
  const waterFactor = Math.max(0.5, water / 1000)
  return lightFactor * waterFactor
}

function bezierInterpolate(p0: { x: number; y: number }, p1: { x: number; y: number }, p2: { x: number; y: number }, p3: { x: number; y: number }, t: number): { x: number; y: number } {
  const t2 = t * t
  const t3 = t2 * t
  const mt = 1 - t
  const mt2 = mt * mt
  const mt3 = mt2 * mt
  return {
    x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
    y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y,
  }
}

function generateBezierPath(start: { x: number; y: number }, end: { x: number; y: number }, curvature: number = 0.3): { x: number; y: number }[] {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const dist = Math.hypot(dx, dy)
  const perpX = -dy / dist
  const perpY = dx / dist
  const offset = dist * curvature * (Math.random() > 0.5 ? 1 : -1)
  
  const midX = (start.x + end.x) / 2
  const midY = (start.y + end.y) / 2
  
  const cp1 = { x: midX - dx * 0.25 + perpX * offset * 0.5, y: midY - dy * 0.25 + perpY * offset * 0.5 }
  const cp2 = { x: midX + dx * 0.25 + perpX * offset, y: midY + dy * 0.25 + perpY * offset }
  
  const segments = 20
  const points: { x: number; y: number }[] = []
  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    points.push(bezierInterpolate(start, cp1, cp2, end, t))
  }
  return points
}

function swimEasing(t: number): number {
  const accelDuration = 0.15
  const decelStart = 0.75
  
  if (t < accelDuration) {
    const progress = t / accelDuration
    return progress * progress * (3 - 2 * progress)
  }
  if (t > decelStart) {
    const progress = (t - decelStart) / (1 - decelStart)
    const eased = 1 - (1 - progress) * (1 - progress)
    return 0.85 + 0.15 * eased
  }
  return 0.85 + 0.15 * ((t - accelDuration) / (decelStart - accelDuration))
}

export function Tank({ fish, lighting, followMode, feedSignal, lowStatHint, stats, water, decorations, selectedDecoration, backgroundState, onEat, onTease, onPetTrickle, onPlaceDecoration, onCancelPlacement, growthScale, growthStage, fishLevelUpTick }: TankProps) {
  const tankRef = useRef<HTMLDivElement>(null)
  // const [target, setTarget] = useState({ x: 50, y: 42 })
  const [currentPos, setCurrentPos] = useState({ x: 50, y: 42 })
  // const [duration, setDuration] = useState(3)
  const [facing, setFacing] = useState(1) // 1=朝左(原图朝左) -1=朝右
  const [tiltAngle, setTiltAngle] = useState(0) // 转向时的倾斜角度
  const [isTurning, setIsTurning] = useState(false) // 是否正在执行180度转向
  const [turnKey, setTurnKey] = useState(0) // 触发鱼图重新挂载以重启动画
  const [pellets, setPellets] = useState<Pellet[]>([])
  const [ripples, setRipples] = useState<Ripple[]>([])
  const [floats, setFloats] = useState<FloatText[]>([])
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const [burst, setBurst] = useState<{ id: number; x: number; y: number }[]>([])
  const [chomp, setChomp] = useState(0)
  const [excited, setExcited] = useState(false)
  const [idleState, setIdleState] = useState<IdleState>('active')
  const [driftOffset, setDriftOffset] = useState({ x: 0, y: 0 })

  const posRef = useRef({ x: 50, y: 42 })
  const seekingRef = useRef<number | null>(null)
  const lastMoveRef = useRef(0)
  const pelletsRef = useRef<Pellet[]>([])
  const isSwimmingRef = useRef(false)
  const idleTimeoutRef = useRef<number | null>(null)
  const prevFacingRef = useRef(1) // 记录转向前的朝向,用于CSS动画
  const turnTimerRef = useRef<number | null>(null)
  const firstMountDoneRef = useRef(false)
  pelletsRef.current = pellets

  const swimTo = useCallback((nx: number, ny: number, speedMultiplier = 1) => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current)
      idleTimeoutRef.current = null
    }
    setIdleState('active')
    setDriftOffset({ x: 0, y: 0 })
    
    const [minDepth, maxDepth] = fish.depthRange
    nx = Math.min(86, Math.max(12, nx))
    ny = Math.min(Math.min(80, maxDepth), Math.max(Math.max(20, minDepth), ny))
    
    const start = { x: posRef.current.x, y: posRef.current.y }
    const end = { x: nx, y: ny }
    const dx = end.x - start.x
    const dy = end.y - start.y
    const dist = Math.hypot(dx, dy)
    
    if (dist < 3) return
    
    if (Math.abs(dx) > 1.5) {
      const newFacing = dx < 0 ? 1 : -1
      if (newFacing !== facing) {
        prevFacingRef.current = facing
        setFacing(newFacing)
        setIsTurning(true)
        setTurnKey((k) => k + 1)
        if (turnTimerRef.current) clearTimeout(turnTimerRef.current)
        turnTimerRef.current = window.setTimeout(() => setIsTurning(false), 400)
      }
      const behavior = getBehaviorParams(fish.trait)
      const targetTilt = -newFacing * behavior.turnTilt * Math.sign(dy) * 0.6
      setTiltAngle(targetTilt)
      // 倾斜角随游动向水平位置回归
      setTimeout(() => setTiltAngle(0), 500)
    }
    
    const behavior = getBehaviorParams(fish.trait)
    const stateMultiplier = getStateSpeedMultiplier(stats, behavior)
    const envMultiplier = getEnvironmentModifier(lighting, water)
    const baseSpeed = fish.swimSpeed * 16 * stateMultiplier * envMultiplier
    const adjustedSpeed = baseSpeed * speedMultiplier
    const rawDuration = dist / adjustedSpeed
    const finalDuration = Math.min(8, Math.max(0.8, rawDuration))
    
    const curvature = speedMultiplier > 1.5 ? 0.2 : rand(0.25, 0.45)
    const path = generateBezierPath(start, end, curvature)
    
    setCurrentPos(start)
    posRef.current = end
    isSwimmingRef.current = true
    
    let frame = 0
    const totalFrames = path.length - 1
    const startTime = performance.now()
    const totalTime = finalDuration * 1000
    
    const animate = () => {
      const elapsed = performance.now() - startTime
      const t = Math.min(1, elapsed / totalTime)
      const easedT = swimEasing(t)
      frame = Math.floor(easedT * totalFrames)
      
      const currentPoint = path[frame]
      setCurrentPos(currentPoint)
      
      if (t < 1) {
        requestAnimationFrame(animate)
      } else {
        isSwimmingRef.current = false
        setCurrentPos(end)
      }
    }
    
    requestAnimationFrame(animate)
  }, [fish, stats, lighting, water])

  const addFloat = useCallback((x: number, y: number, text: string, color: string) => {
    const id = uid++
    setFloats((f) => [...f, { id, x, y, text, color }])
    setTimeout(() => setFloats((f) => f.filter((i) => i.id !== id)), 1400)
  }, [])

  const spawnBubbles = useCallback((x: number, y: number, n = 5) => {
    const items = Array.from({ length: n }, () => ({
      id: uid++, x: x + rand(-4, 4), y: y + rand(-3, 3),
    }))
    setBurst((b) => [...b, ...items])
    setTimeout(() => setBurst((b) => b.filter((i) => !items.some((t) => t.id === i.id))), 1200)
  }, [])

  // ---------- 游动行为 ----------
  // 随机漫游
  useEffect(() => {
    if (followMode) return
    const wander = () => {
      if (pelletsRef.current.length > 0) return // 有饲料优先吃
      if (isSwimmingRef.current) return
      if (idleState !== 'active') return
      const [minDepth, maxDepth] = fish.depthRange
      const behavior = getBehaviorParams(fish.trait)
      const stateMultiplier = getStateSpeedMultiplier(stats, behavior)
      
      if (Math.random() < behavior.idleChance) {
        enterIdleState()
        return
      }
      
      swimTo(rand(20, 80), rand(minDepth, maxDepth), stateMultiplier)
    }
    const behavior = getBehaviorParams(fish.trait)
    const interval = behavior.wanderInterval
    const id = setInterval(wander, interval)
    const t0 = setTimeout(wander, 400)
    return () => { clearInterval(id); clearTimeout(t0) }
  }, [followMode, swimTo, fish, stats, idleState])

  // 空闲状态系统
  const enterIdleState = useCallback(() => {
    const behavior = getBehaviorParams(fish.trait)
    const [, maxDepth] = fish.depthRange
    
    if (stats.energy < 300 && Math.random() < behavior.restChance) {
      setIdleState('rest')
      swimTo(rand(20, 80), maxDepth, 0.5)
      
      let restFrame = 0
      const restDuration = rand(6000, 10000)
      const restTotalFrames = restDuration / 100
      const restDriftSpeed = behavior.driftSpeed * 0.5
      
      const restDrift = () => {
        if (idleState !== 'rest') return
        restFrame++
        const t = restFrame / restTotalFrames
        const driftX = Math.sin(t * Math.PI * 0.8) * restDriftSpeed * 4
        const driftY = Math.cos(t * Math.PI * 1.2) * restDriftSpeed * 2
        setDriftOffset({ x: driftX, y: driftY })
        
        if (restFrame < restTotalFrames) {
          setTimeout(restDrift, 100)
        } else {
          setDriftOffset({ x: 0, y: 0 })
          setIdleState('active')
        }
      }
      restDrift()
    } else if (Math.random() < 0.35) {
      setIdleState('hover')
      const hoverDuration = rand(3000, 5000)
      let hoverFrame = 0
      const hoverTotalFrames = hoverDuration / 60
      const hoverDriftSpeed = behavior.driftSpeed * 0.8
      
      const hoverDrift = () => {
        if (idleState !== 'hover') return
        hoverFrame++
        const t = hoverFrame / hoverTotalFrames
        const driftX = Math.sin(t * Math.PI * 1.5) * hoverDriftSpeed * 6
        const driftY = Math.cos(t * Math.PI * 2) * hoverDriftSpeed * 3
        setDriftOffset({ x: driftX, y: driftY })
        
        if (hoverFrame < hoverTotalFrames) {
          setTimeout(hoverDrift, 60)
        } else {
          setDriftOffset({ x: 0, y: 0 })
          setIdleState('active')
        }
      }
      hoverDrift()
    } else {
      setIdleState('drift')
      const [minDepth, maxDepth] = fish.depthRange
      const currentX = posRef.current.x
      const currentY = posRef.current.y
      const targetX = Math.max(15, Math.min(85, currentX + rand(-25, 25)))
      const targetY = Math.max(minDepth + 5, Math.min(maxDepth - 5, currentY + rand(-15, 15)))
      
      swimTo(targetX, targetY, behavior.driftSpeed * 0.6)
      
      idleTimeoutRef.current = window.setTimeout(() => {
        setDriftOffset({ x: 0, y: 0 })
        setIdleState('active')
      }, rand(4000, 7000))
    }
  }, [fish, stats, swimTo])

  // 投喂信号 → 生成饲料
  useEffect(() => {
    if (feedSignal === 0) return
    const news: Pellet[] = Array.from({ length: 4 }, () => ({
      id: uid++, x: rand(22, 78), y: rand(52, 74),
    }))
    setPellets((p) => [...p, ...news])
  }, [feedSignal])
// 追最近的饲料
  
  const seekNearest = useCallback(() => {
    const ps = pelletsRef.current
    if (ps.length === 0) { seekingRef.current = null; return }
    const cur = posRef.current
    let best = ps[0]; let bd = Infinity
    for (const p of ps) {
      const d = Math.hypot(p.x - cur.x, p.y - cur.y)
      if (d < bd) { bd = d; best = p }
    }
    seekingRef.current = best.id
    const hungerFactor = stats.satiety < 300 ? 1.4 : stats.satiety < 600 ? 1.1 : 1.0
    swimTo(best.x, best.y - 3, 1.9 * fish.swimSpeed * hungerFactor)
  }, [swimTo, fish, stats])

  // 有饲料 → 追最近的
  useEffect(() => {
    if (pellets.length === 0) { seekingRef.current = null; return }
    if (seekingRef.current !== null || followMode) return
    if (isSwimmingRef.current) return
    seekNearest()
  }, [pellets, followMode, seekNearest])

  // 到达目标点回调
  useEffect(() => {
    if (isSwimmingRef.current) return
    const sid = seekingRef.current
    if (sid === null) {
      // 漫游/逗鱼结束后,若还有饲料则继续吃
      if (pelletsRef.current.length > 0) seekNearest()
      return
    }
    const p = pelletsRef.current.find((i) => i.id === sid)
    if (!p) { seekingRef.current = null; return }
    const d = Math.hypot(p.x - posRef.current.x, p.y - posRef.current.y)
    if (d > 16) { seekingRef.current = null; return }
    seekingRef.current = null
    setPellets((prev) => prev.filter((i) => i.id !== sid))
    setChomp((c) => c + 1)
    spawnBubbles(p.x, p.y - 4, 4)
    addFloat(p.x, p.y - 10, '+饱食', '#d97706')
    onEat()
  }, [currentPos, isSwimmingRef.current, addFloat, onEat, spawnBubbles, seekNearest])

  // 逗鱼模式:跟随手指/鼠标
  const toPercent = useCallback((e: React.PointerEvent) => {
    const rect = tankRef.current!.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    }
  }, [])

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!followMode) return
      const now = Date.now()
      if (now - lastMoveRef.current < 160) return
      lastMoveRef.current = now
      const { x, y } = toPercent(e)
      const moodFactor = stats.mood > 700 ? 1.2 : stats.mood > 400 ? 1.0 : 0.8
      swimTo(x, y, 2.1 * moodFactor)
    },
    [followMode, swimTo, toPercent, stats],
  )

  // 逗鱼模式亲密度滴灌
  useEffect(() => {
    if (!followMode) return
    const id = setInterval(onPetTrickle, 1000)
    return () => clearInterval(id)
  }, [followMode, onPetTrickle])

  // 点击水面:涟漪 + 鱼游过来 + 奖励 或放置装饰
  const handleTap = useCallback((e: React.PointerEvent) => {
      const { x, y } = toPercent(e)
      const id = uid++
      setRipples((r) => [...r, { id, x, y }])
      setTimeout(() => setRipples((r) => r.filter((i) => i.id !== id)), 1100)
      
      if (selectedDecoration) {
        onPlaceDecoration(x, y)
        return
      }
      
      const energyFactor = stats.energy > 600 ? 1.0 : stats.energy > 300 ? 0.85 : 0.65
      const behavior = getBehaviorParams(fish.trait)
      const bondFactor = 0.7 + (stats.bond / 1000) * behavior.bondResponse
      swimTo(x, y, 2.4 * energyFactor * bondFactor)
      
      const bubbleCount = stats.bond > 700 ? 10 : stats.bond > 400 ? 6 : 4
      spawnBubbles(x, y, bubbleCount)
      
      if (stats.bond > 800) {
        addFloat(x, y - 8, '最喜欢你了!', '#ec4899')
        setTimeout(() => addFloat(x, y - 15, '+亲密', '#e11d48'), 200)
      } else if (stats.bond > 500) {
        addFloat(x, y - 8, '开心!', '#f59e0b')
        setTimeout(() => addFloat(x, y - 15, '+亲密', '#e11d48'), 200)
      } else {
        addFloat(x, y - 8, '+亲密', '#e11d48')
      }
      setExcited(true)
      setTimeout(() => setExcited(false), 1200)
      onTease()
    },
    [toPercent, swimTo, spawnBubbles, addFloat, onTease, stats, fish, selectedDecoration, onPlaceDecoration],
  )

  // 环境气泡
  useEffect(() => {
    const id = setInterval(() => {
      setBubbles((b) => {
        const nb: Bubble = { id: uid++, x: rand(4, 96), y: rand(80, 96), size: rand(5, 14), dur: rand(4, 7), drift: rand(-14, 14) }
        const next = [...b, nb]
        return next.length > 16 ? next.slice(next.length - 16) : next
      })
    }, 900)
    return () => clearInterval(id)
  }, [])

  const night = lighting === 'night'
  const hasCustomBg = backgroundState.enabled && (backgroundState.imageData || backgroundState.presetId)

  const getBackgroundImage = () => {
    if (!backgroundState.enabled) return null
    if (backgroundState.imageData) return backgroundState.imageData
    if (backgroundState.presetId === 'stone') {
      return new URL('/src/assets/background/bg-stone.jpg', import.meta.url).href
    }
    return null
  }

  return (
    <div
      ref={tankRef}
      className={`absolute inset-0 overflow-hidden select-none touch-none transition-colors duration-1000 ${
        hasCustomBg ? '' : night ? 'tank-night' : 'tank-day'
      }`}
      onPointerMove={handlePointerMove}
      onPointerDown={handleTap}
    >
      {hasCustomBg && (() => {
        const backgroundImage = getBackgroundImage()
        return backgroundImage ? (
          <div
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: backgroundState.fitMode,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: backgroundState.opacity,
              filter: night ? 'brightness(0.6) saturate(0.8)' : 'none',
            }}
          />
        ) : null
      })()}

      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: night
            ? 'linear-gradient(180deg, rgba(5,15,35,0.6) 0%, rgba(10,25,50,0.4) 100%)'
            : 'linear-gradient(180deg, rgba(20,60,100,0.5) 0%, rgba(30,90,150,0.3) 100%)',
        }}
      />

      {/* 光束 */}
      <div className="light-ray" style={{ left: '16%', opacity: hasCustomBg ? 0.5 : 1 }} />
      <div className="light-ray" style={{ left: '44%', animationDelay: '-3s', opacity: hasCustomBg ? 0.3 : 0.5 }} />
      <div className="light-ray" style={{ left: '72%', animationDelay: '-6s', opacity: hasCustomBg ? 0.5 : 1 }} />

      {/* 装饰物品 */}
      {decorations.map((placed, index) => {
        const decoration = DECORATIONS.find(d => d.id === placed.decorationId)
        if (!decoration) return null
        
        return (
          <motion.div
            key={index}
            className="absolute pointer-events-none"
            style={{
              left: `${placed.x}%`,
              top: `${placed.y}%`,
              transform: `translate(-50%, -50%) rotate(${placed.rotation}deg)`,
              fontSize: decoration.category === 'background' ? '150px' : '48px',
              zIndex: decoration.zIndex,
              opacity: decoration.category === 'background' ? 0.3 : 0.8,
            }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: decoration.category === 'background' ? 0.3 : 0.8, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {decoration.emoji}
          </motion.div>
        )
      })}

      {/* 装饰放置提示 */}
      <AnimatePresence>
        {selectedDecoration && (
          <motion.div
            key="placement-hint"
            className="absolute top-4 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full bg-sky-500/90 text-white text-xs font-semibold tracking-widest shadow-lg backdrop-blur flex items-center gap-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <span className="text-2xl">{selectedDecoration.emoji}</span>
            <span>点击放置装饰</span>
            <button
              onClick={onCancelPlacement}
              className="ml-2 px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs"
            >
              取消
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 水面波纹线 */}
      <div
        className="absolute top-[10%] left-0 right-0 h-px"
        style={{
          background: 'white',
          opacity: hasCustomBg ? 0.2 : 0.4,
          boxShadow: hasCustomBg ? 'none' : '0 1px 12px rgba(255,255,255,.6)',
        }}
      />

      {/* 底部发光圆台 */}
      {!hasCustomBg && (
        <div className="absolute bottom-[4%] left-1/2 -translate-x-1/2 w-[62%] h-[16%] pointer-events-none">
          <div className={`platform-ring ${night ? 'platform-ring-night' : ''}`} />
        </div>
      )}

      {bubbles.map((b) => (
        <span
          key={b.id}
          className="ambient-bubble"
          style={{
            left: `${b.x}%`,
            width: b.size,
            height: b.size,
            animationDuration: `${b.dur}s`,
            ['--drift' as string]: `${b.drift}px`,
          }}
          onAnimationEnd={() => setBubbles((prev) => prev.filter((i) => i.id !== b.id))}
        />
      ))}

      {/* 饲料 */}
      {pellets.map((p) => (
        <motion.span
          key={p.id}
          className="pellet"
          style={{ left: `${p.x}%` }}
          initial={{ top: '-4%' }}
          animate={{ top: `${p.y}%` }}
          transition={{ duration: 2.4, ease: 'easeIn' }}
        />
      ))}

      {/* 鱼 */}
      <motion.div
        className="absolute z-10 pointer-events-none"
        style={{ width: `min(${34 * growthScale}%, ${360 * growthScale}px)` }}
        animate={{
          left: `${currentPos.x + driftOffset.x}%`,
          top: `${currentPos.y + driftOffset.y}%`,
        }}
        transition={{ duration: 0.05 }}
      >
        <motion.div
          className="-translate-x-1/2 -translate-y-1/2"
          animate={{
            y: [0, -9, 0],
            rotate: [0, 2.5, 0, -2.5, 0],
            rotateZ: tiltAngle,
            scale: excited ? [1, 1.06, 1] : idleState === 'rest' ? 0.92 : 1,
          }}
          transition={{
            repeat: Infinity,
            duration: idleState === 'hover' ? 6 : idleState === 'rest' ? 8 : 4,
            ease: 'easeInOut',
            rotateZ: { duration: 0.3, ease: 'easeOut' },
            scale: { duration: 0.8, ease: 'easeInOut' },
          }}
        >
          {growthStage >= 4 && (
            <motion.div
              className="absolute -top-8 left-1/2 -translate-x-1/2 z-20"
              animate={{
                y: [0, -4, 0],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut',
              }}
            >
              <Sparkles size={28} className="text-amber-400 drop-shadow-lg" />
            </motion.div>
          )}
          <motion.img
            key={`${fish.id}-${turnKey}`}
            src={fish.img}
            alt={fish.name}
            draggable={false}
            className={`fish-img w-full drop-shadow-[0_18px_24px_rgba(30,80,140,0.25)] ${growthStage >= 4 ? 'drop-shadow-[0_20px_30px_rgba(251,191,36,0.3)]' : ''} ${isTurning ? 'turning' : ''}`}
            style={{
              '--facing': facing,
              '--wag-speed': `${0.8 / getBehaviorParams(fish.trait).tailWagSpeed}s`,
              '--old-facing': prevFacingRef.current,
              '--new-facing': facing,
              '--turn-duration': '0.4s',
              '--turn-sign': -facing,
              mixBlendMode: 'multiply',
            } as React.CSSProperties}
            initial={firstMountDoneRef.current ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: firstMountDoneRef.current ? 0 : 0.5 }}
            onAnimationComplete={() => { firstMountDoneRef.current = true }}
          />
          {chomp > 0 && (
            <motion.span
              key={chomp}
              className="absolute text-2xl"
              style={{ [facing === 1 ? 'left' : 'right']: '-6%', top: '18%' } as React.CSSProperties}
              initial={{ opacity: 1, scale: 0.4, y: 6 }}
              animate={{ opacity: 0, scale: 1.3, y: -22 }}
              transition={{ duration: 1 }}
            >
              💛
            </motion.span>
          )}
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {fishLevelUpTick > 0 && (
          <motion.div
            key={fishLevelUpTick}
            className="absolute inset-0 pointer-events-none z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="text-center"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.2, opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <motion.div
                className="text-6xl mb-2"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0],
                }}
                transition={{
                  repeat: 3,
                  duration: 0.4,
                }}
              >
                🎉
              </motion.div>
              <motion.div
                className="text-white text-xl font-bold drop-shadow-lg"
                animate={{
                  y: [0, -8, 0],
                  opacity: [0.8, 1, 0.8],
                }}
                transition={{
                  repeat: 3,
                  duration: 0.6,
                }}
              >
                成长升级！
              </motion.div>
              <motion.div
                className="text-amber-300 text-sm mt-1"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {fish.name} 变得更大了！
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 状态提示气泡 */}
      <AnimatePresence>
        {lowStatHint && (
          <motion.div
            key={lowStatHint}
            className="absolute z-20 px-3 py-1.5 rounded-full text-xs font-medium bg-white/85 backdrop-blur border border-white shadow-md text-slate-600"
            style={{ left: `${currentPos.x}%`, top: `${Math.max(6, currentPos.y - 22)}%`, transform: 'translateX(-50%)' }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {lowStatHint}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 涟漪 */}
      {ripples.map((r) => (
        <span key={r.id} className="ripple" style={{ left: `${r.x}%`, top: `${r.y}%` }} />
      ))}

      {burst.map((b) => (
        <span key={b.id} className="burst-bubble" style={{ left: `${b.x}%`, top: `${b.y}%` }} />
      ))}

      {/* 漂浮文字 / 爱心 */}
      {floats.map((f) => (
        <motion.span
          key={f.id}
          className="absolute z-30 flex items-center gap-1 text-sm font-bold pointer-events-none"
          style={{ left: `${f.x}%`, top: `${f.y}%`, color: f.color }}
          initial={{ opacity: 0, y: 0, scale: 0.7 }}
          animate={{ opacity: [0, 1, 1, 0], y: -56, scale: 1 }}
          transition={{ duration: 1.35, ease: 'easeOut' }}
        >
          <Heart size={14} fill="currentColor" /> {f.text}
        </motion.span>
      ))}

      {/* 逗鱼模式提示 */}
      <AnimatePresence>
        {followMode && (
          <motion.div
            className="absolute top-4 left-1/2 -translate-x-1/2 z-30 px-4 py-1.5 rounded-full bg-sky-500/85 text-white text-xs font-semibold tracking-widest shadow-lg backdrop-blur"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            逗鱼模式中 · 移动手指/鼠标,它会跟着你
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}